/**
 * Die Stückliste eines Packstücks oder Lagerorts (ADR 0013 §12, ADR 0017).
 *
 * **Grundlage ist der aktuelle Inhalt:** alles, was gerade darin gebucht ist,
 * mit oder ohne Stammplatz (ADR 0019, so vom Nutzer entschieden). Anfangs war
 * es der Soll-Inhalt (ADR 0013 §12) -- auf der Liste fehlte dann, was keinen
 * Stammplatz in dieser Kiste hat, obwohl es darin liegt. Was fehlt, zeigt
 * weiter die Vollständigkeit auf dem Bildschirm.
 *
 * Aus der lokalen Kopie gerechnet, also auch ohne Netz. Das PDF macht
 * `druck/stueckliste.ts`.
 */

import type { Lagerort } from "@thw/contracts";
import { stammplatzPfad } from "@thw/core-domain";
import { behaelterAnsicht } from "./behaelter.js";
import type { Bestand } from "./bestand.js";

/** Gleiche Artikel in einer Zeile: „5× Leitung CEE 125 A“. */
export interface ListenGruppe {
  art: "gruppe";
  bezeichnung: string;
  /** Aufsteigend -- so lassen sie sich zusammenziehen (`nummernKurz`). */
  nummern: string[];
}

/** Ein Platz oder Packstück darin: „WK-03 Werkzeugkiste (eigene Liste)“. */
export interface ListenUnterort {
  art: "unterort";
  lagerort: Lagerort;
}

export type ListenZeile = ListenGruppe | ListenUnterort;

export interface Stueckliste {
  lagerort: Lagerort;
  /** Der Weg zum Stammplatz des Packstücks; leer ohne und bei festen Orten. */
  stammplatz: string[];
  /** Erst die Artikel, dann Plätze und Packstücke darin. */
  zeilen: ListenZeile[];
  /** Teile in den Artikelzeilen -- ohne Plätze und Packstücke. */
  teile: number;
}

const nachName = (a: string, b: string): number => a.localeCompare(b, "de");
const nachNummer = (a: string, b: string): number => a.localeCompare(b, "de", { numeric: true });

/** Artikel nach Bezeichnung zusammengefasst, beide Ebenen sortiert. */
function gruppieren(artikel: readonly { bezeichnung: string; inventarnummer: string }[]) {
  const gruppen = new Map<string, string[]>();
  for (const a of artikel) {
    const liste = gruppen.get(a.bezeichnung);
    if (liste === undefined) gruppen.set(a.bezeichnung, [a.inventarnummer]);
    else liste.push(a.inventarnummer);
  }
  return [...gruppen]
    .sort(([a], [b]) => nachName(a, b))
    .map(([bezeichnung, nummern]): ListenGruppe => ({
      art: "gruppe",
      bezeichnung,
      nummern: nummern.sort(nachNummer),
    }));
}

/** Plätze zuerst, dann Packstücke, jeweils nach Kennung. */
function unterortZeilen(orte: Iterable<Lagerort>): ListenUnterort[] {
  return [...orte]
    .sort(
      (a, b) =>
        Number(a.artikel_id !== null) - Number(b.artikel_id !== null) ||
        nachName(a.kennung, b.kennung),
    )
    .map((lagerort) => ({ art: "unterort", lagerort }));
}

export function stueckliste(bestand: Bestand, lagerort_id: string): Stueckliste | null {
  const ansicht = behaelterAnsicht(bestand, lagerort_id);
  if (ansicht === null) return null;
  const { lagerort } = ansicht;

  const eigenerStammplatz =
    lagerort.artikel_id === null
      ? null
      : (bestand.artikel.get(lagerort.artikel_id)?.stammplatz_id ?? null);
  const stammplatz =
    eigenerStammplatz === null ? [] : stammplatzPfad(eigenerStammplatz, bestand.lagerorte);

  // Was gerade darin gebucht ist -- auch fremde Teile und solche ohne
  // Stammplatz. Archiviertes (verloren, gestohlen) ist nicht wirklich darin.
  const gruppen = gruppieren(
    ansicht.gruppen.flatMap((g) =>
      g.eintraege.filter((e) => !e.zustand.archiviert).map((e) => e.artikel),
    ),
  );
  return {
    lagerort,
    stammplatz,
    zeilen: [
      ...gruppen,
      // Feste Plätze darin und die Packstücke, die gerade darin stecken.
      ...unterortZeilen([
        ...ansicht.plaetze.map((p) => p.lagerort),
        ...ansicht.packstuecke.map((p) => p.lagerort),
      ]),
    ],
    teile: gruppen.reduce((s, g) => s + g.nummern.length, 0),
  };
}

/** Die Plätze und Packstücke auf einer Liste. */
export function unterorteDerListe(liste: Stueckliste): Lagerort[] {
  return liste.zeilen.flatMap((z) => (z.art === "unterort" ? [z.lagerort] : []));
}

/**
 * Die Liste selbst und, auf Wunsch, die Listen der Plätze und Packstücke
 * darin -- der Reihe nach, jede Ebene unter ihrer oberen. Ein Kreis (nach
 * widerstreitenden Offline-Buchungen möglich) druckt nichts doppelt.
 */
export function stuecklisten(
  bestand: Bestand,
  lagerort_id: string,
  mitUnterlisten: boolean,
): Stueckliste[] {
  const gesehen = new Set<string>();
  const listen: Stueckliste[] = [];
  const sammeln = (id: string): void => {
    if (gesehen.has(id)) return;
    gesehen.add(id);
    const liste = stueckliste(bestand, id);
    if (liste === null) return;
    listen.push(liste);
    if (mitUnterlisten) for (const u of unterorteDerListe(liste)) sammeln(u.id);
  };
  sammeln(lagerort_id);
  return listen;
}

/**
 * Nummern zusammengezogen wie auf der Skizze: „0141-029281 · -82 · -83“.
 *
 * Jede Nummer nach der ersten ersetzt nur die letzten Stellen ihrer
 * Vorgängerin -- mindestens zwei, damit „-2“ nicht wie ein Tippfehler
 * aussieht. Wo sich schon der Teil vor dem Bindestrich unterscheidet, oder
 * die Länge, steht die Nummer ganz da.
 */
export function nummernKurz(nummern: readonly string[]): string[] {
  const aus: string[] = [];
  let vorige: string | null = null;
  for (const n of nummern) {
    const strich = n.indexOf("-");
    if (vorige === null || vorige.length !== n.length || strich < 0) {
      aus.push(n);
    } else {
      let gleich = 0;
      while (gleich < n.length && n[gleich] === vorige[gleich]) gleich += 1;
      const schnitt = Math.min(gleich, n.length - 2);
      aus.push(schnitt > strich ? `-${n.slice(schnitt)}` : n);
    }
    vorige = n;
  }
  return aus;
}
